/**
 * @file Expert dashboard UI — pages/Dashboard/Expert/AllDataset/ExpertAllDataset.tsx.
 */

// src/pages/Dashboard/Expert/AllDataset/ExpertAllDataset.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AnalysisBanner from "../../../../components/common/AnalysisBanner";
import {
  CementBoxPlotChart,
  type CementBoxPlotSlotSelection,
} from "../../../../components";
import ExpertChartSlotBanner from "../components/ExpertChartSlotBanner";
import ExpertDatasetSearchBar from "../components/ExpertDatasetSearchBar";
import {
  enrichExpertDatasets,
  filterExpertDatasetTableRows,
  filterRowsByChartSlot,
  getExpertDatasetsByMaterial,
  mapExpertDatasetToTableRow,
  toggleExpertChartSlotSelection,
  type ExpertDataset,
  type ExpertDatasetTableRow,
} from "../../../../services/expertDatasetService";
import "./ExpertAllDataset.css";

interface Props {
  materialId?: number | null;
}

const PAGE_SIZE = 50;

const fmtNum = (v: number | null | undefined): string =>
  v != null ? v.toFixed(3) : "-";

const ExpertAllDataset: React.FC<Props> = ({ materialId }) => {
  const [datasets, setDatasets] = useState<ExpertDataset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [materialName, setMaterialName] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [chartSlotSelection, setChartSlotSelection] =
    useState<CementBoxPlotSlotSelection | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
    setSearchTerm("");
    setChartSlotSelection(null);
  }, [materialId]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, chartSlotSelection]);

  const handleChartSlotClick = useCallback((detail: CementBoxPlotSlotSelection) => {
    setChartSlotSelection((prev) => toggleExpertChartSlotSelection(prev, detail));
  }, []);

  const clearChartSlotSelection = useCallback(() => setChartSlotSelection(null), []);

  const bannerTitle = materialName ? `All Datasets: ${materialName}` : "All Datasets";

  const setMaterialNameIfEmpty = useCallback((name: string) => {
    if (!name) return;
    setMaterialName((prev) => prev || name);
  }, []);

  const enrichDatasets = useCallback(async (raw: ExpertDataset[]) => {
    return enrichExpertDatasets(raw);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const apiRows = await getExpertDatasetsByMaterial();

        const filtered =
          materialId !== null && materialId !== undefined
            ? apiRows.filter((d) => d.background?.materialId === materialId)
            : apiRows;

        const enriched = await enrichDatasets(filtered);
        if (cancelled) return;
        setDatasets(enriched);

        const fromData =
          enriched.find((d) => d.background?.material?.name)?.background?.material?.name ||
          "";
        setMaterialNameIfEmpty(fromData);
      } catch (err: any) {
        console.error(err);
        if (cancelled) return;
        setDatasets([]);
        setError(err?.response?.data?.message || "Failed to load datasets.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enrichDatasets, materialId, setMaterialNameIfEmpty]);

  const rows = useMemo(
    () => datasets.map((d) => mapExpertDatasetToTableRow(d)),
    [datasets]
  );

  const searchFilteredRows = useMemo(
    () => filterExpertDatasetTableRows(rows, searchTerm),
    [rows, searchTerm]
  );

  const rowsFilteredByChart = useMemo(
    () => filterRowsByChartSlot(searchFilteredRows, chartSlotSelection),
    [searchFilteredRows, chartSlotSelection]
  );

  const tableEmptyMessage =
    chartSlotSelection && rowsFilteredByChart.length === 0 && rows.length > 0
      ? "No datasets in this chart column match your current search. Clear the chart filter or adjust your search."
      : "No datasets match your search. Try a different submission code, contributor name, or email.";

  const totalItems = rowsFilteredByChart.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return rowsFilteredByChart.slice(start, start + PAGE_SIZE);
  }, [rowsFilteredByChart, page]);

  if (loading && datasets.length === 0) {
    return (
      <div className="background-form-container analysis-shell">
        <AnalysisBanner title={bannerTitle} />
        <p className="all-dataset-info-text">Loading datasets...</p>
      </div>
    );
  }

  if (error && datasets.length === 0) {
    return (
      <div className="background-form-container analysis-shell">
        <AnalysisBanner title={bannerTitle} />
        <p className="all-dataset-info-text">{error}</p>
      </div>
    );
  }

  return (
    <div className="background-form-container analysis-shell">
      <AnalysisBanner title={bannerTitle} />
      <div className="all-dataset-overview-container">
        <div className="material-overview-graphs">
          <div className="graph-card data-visualisation-wrapper">
            {loading ? (
              <p className="all-dataset-info-text">Loading graph...</p>
            ) : error ? (
              <p className="all-dataset-info-text" style={{ color: "#d32f2f" }}>
                {error}
              </p>
            ) : (
              <>
                <h2 className="material-overview-graph-title">
                  Comparison of embodied impacts across categorical tiers
                </h2>
                <p className="material-overview-graph-hint material-overview-graph-hint--clickable">
                  Click any chart column to filter submissions in that group (same
                  sub-type, technology, time bucket, and metric as the chart).
                  Values outside the whiskers appear as dots. Click the column again
                  to clear.
                </p>
                <CementBoxPlotChart
                  datasets={datasets}
                  showFilters={(datasets?.length ?? 0) > 0}
                  parentMaterialName={materialName}
                  interactiveSlotSelection
                  onSlotClick={handleChartSlotClick}
                  onInteractiveMetricChange={clearChartSlotSelection}
                />
              </>
            )}
          </div>
        </div>

        {chartSlotSelection ? (
          <ExpertChartSlotBanner
            chartSlotSelection={chartSlotSelection}
            visibleCount={rowsFilteredByChart.length}
            visibleSubmissionCodes={rowsFilteredByChart.map(
              (r) => r.submissionCode || r.submissionId
            )}
            onClear={clearChartSlotSelection}
          />
        ) : null}

        {rows.length > 0 ? (
          <ExpertDatasetSearchBar value={searchTerm} onChange={setSearchTerm} />
        ) : null}

        <div className="all-dataset-table-card">
        {rows.length === 0 ? (
          <div className="all-dataset-empty" role="status" aria-live="polite">
            <div className="empty-icon" aria-hidden="true">
              <svg width="58" height="58" viewBox="0 0 60 60" focusable="false">
                <g fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="16" width="42" height="30" rx="6" ry="6" />
                  <path d="M9 24h42" />
                  <circle cx="20" cy="38" r="3" />
                  <path d="M27 40h18" />
                </g>
              </svg>
            </div>
            <h3 className="empty-title">No datasets yet</h3>
            <p className="empty-desc">
              You will see datasets here as soon as contributors add them.
            </p>
          </div>
        ) : (
          <>
            <h3 className="all-dataset-table-title">All datasets</h3>
            {rowsFilteredByChart.length === 0 ? (
              <p className="all-dataset-search-empty" role="status">
                {tableEmptyMessage}
              </p>
            ) : (
              <div className="all-dataset-table-wrapper">
                <table className="all-dataset-table">
                  <thead>
                    <tr>
                      <th>Submission code</th>
                      <th>Material Sub-type</th>
                      <th>Embodied Carbon</th>
                      <th>Embodied Carbon Unit</th>
                      <th>Data Review</th>
                      <th>Data Completeness</th>
                      <th>Data Acquisition</th>
                      <th>Temporal Representativeness</th>
                      <th>Geographical Representativeness</th>
                      <th>Technological Representativeness</th>
                      <th>Manufacturer Representativeness</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((r: ExpertDatasetTableRow) => (
                      <tr key={r.submissionId}>
                        <td>{r.submissionCode}</td>
                        <td>{r.subMaterial || "-"}</td>
                        <td>{fmtNum(r.embodiedCarbon)}</td>
                        <td>{r.embodiedCarbonUnit || "-"}</td>
                        <td>{r.dataReview || "-"}</td>
                        <td>{r.dataCompleteness || "-"}</td>
                        <td>{r.dataAcquisition || "-"}</td>
                        <td>{r.temporal || "-"}</td>
                        <td>{r.geographical || "-"}</td>
                        <td>{r.technological || "-"}</td>
                        <td>{r.manufacturer || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {rowsFilteredByChart.length > 0 && totalPages > 1 && (
          <div className="data-table-pagination" aria-label="Dataset pagination">
            <div className="pagination-info">
              <span>
                Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
                {Math.min(page * PAGE_SIZE, totalItems)} of {totalItems} entries
              </span>
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Previous
              </button>
              <div className="pagination-numbers">
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (page <= 4) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = page - 3 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`pagination-number ${page === pageNum ? "active" : ""}`}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                className="pagination-btn"
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

/** React component — Expert All Dataset. */
export default ExpertAllDataset;
